import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Login from './views/Login';
import MainLayout from './components/MainLayout';
import AuthLayout from './components/AuthLayout';
import useAuth from './hooks/useAuth';
import { CircularProgress, Backdrop, Box, Typography } from '@mui/material';

import SynthesisConfigurationsList from './views/SynthesisConfigurations/index';
import SynthesisConfigurationDetail from './views/SynthesisConfigurations/SynthesisConfigurationDetail';
import SynthesisConfigurationForm from './views/SynthesisConfigurations/SynthesisConfigurationForm';
import Dashboard from './views/Dashboard/index';
import AudioUpload from './views/AudioUpload';

function CustomRoute({
  isPrivate,
  element,
}: {
  isPrivate?: boolean;
  element: React.ReactElement;
}) {
  const { authenticated, loading } = useAuth();

  if (loading) {
    return (
      <Backdrop open={true} sx={{ zIndex: 1300 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  if (isPrivate && !authenticated) {
    return <Navigate to="/login" replace />;
  }

  return element;
}

const AppRoutes: React.FC = () => {
  const { authenticated, loading } = useAuth();

  if (loading) {
    return (
      <Backdrop open={true} sx={{ zIndex: 1300 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          authenticated ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      <Route
        path="/login"
        element={
          authenticated ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <AuthLayout>
              <Login />
            </AuthLayout>
          )
        }
      />

      {/* Rota para a dashboard */}
      <Route
        path="/dashboard"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <Dashboard />
              </MainLayout>
            }
          />
        }
      />

      {/* Rotas do Synthesis */}
      <Route
        path="/synthesis/configurations"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <SynthesisConfigurationsList />
              </MainLayout>
            }
          />
        }
      />
      <Route
        path="/synthesis/configurations/novo"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <SynthesisConfigurationForm />
              </MainLayout>
            }
          />
        }
      />
      <Route
        path="/synthesis/configurations/:cfgId"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <SynthesisConfigurationDetail />
              </MainLayout>
            }
          />
        }
      />
      <Route
        path="/synthesis/configurations/:cfgId/editar"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <SynthesisConfigurationForm />
              </MainLayout>
            }
          />
        }
      />

      {/* Nova rota para upload de áudio e geração da ata */}
      <Route
        path="/audio-upload"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <AudioUpload />
              </MainLayout>
            }
          />
        }
      />

      <Route
        path="*"
        element={
          <MainLayout>
            <Box sx={{ textAlign: 'center', mt: 5 }}>
              <Typography variant="h4">404 - Página Não Encontrada</Typography>
              <Typography variant="body1" sx={{ mt: 2 }}>
                A página que você está procurando não existe.
              </Typography>
            </Box>
          </MainLayout>
        }
      />
    </Routes>
  );
};

export default AppRoutes;